import { dataService } from '../dataService';
import { db, SYNC_STATUS, SYNC_ACTIONS, ENTITY_TYPES } from '../../db';
import { ordersApi, customersApi, productApi } from '../../api';

const MAX_RETRIES = 5;
const PROCESS_INTERVAL = 30 * 1000; // 30 seconds

const defaultHandlers = {
  [ENTITY_TYPES.ORDER]: {
    [SYNC_ACTIONS.CREATE]: async ({ payload }) => {
      const result = await ordersApi.create(payload.data);
      return { record: result };
    },
    [SYNC_ACTIONS.DELETE]: async ({ entityId }) => {
      await ordersApi.remove(entityId);
      return { deleted: true };
    }
  },
  [ENTITY_TYPES.CUSTOMER]: {
    [SYNC_ACTIONS.CREATE]: async ({ payload }) => {
      const result = await customersApi.create(payload.data);
      return { record: result };
    },
    [SYNC_ACTIONS.UPDATE]: async ({ entityId, payload }) => {
      const result = await customersApi.update(entityId, payload.data);
      return { record: result };
    },
    [SYNC_ACTIONS.DELETE]: async ({ entityId }) => {
      await customersApi.remove(entityId);
      return { deleted: true };
    }
  },
  [ENTITY_TYPES.PRODUCT]: {
    [SYNC_ACTIONS.CREATE]: async ({ payload }) => {
      const result = await productApi.create(payload.data);
      return { record: result };
    },
    [SYNC_ACTIONS.UPDATE]: async ({ entityId, payload }) => {
      const result = await productApi.update(entityId, payload.data);
      return { record: result };
    },
    [SYNC_ACTIONS.DELETE]: async ({ entityId }) => {
      await productApi.delete(entityId);
      return { deleted: true };
    }
  }
};

class SyncService {
  constructor() {
    this.handlers = { ...defaultHandlers };
    this.intervalId = null;
    this.isProcessing = false;
  }

  registerHandler(entityType, action, handler) {
    if (!this.handlers[entityType]) {
      this.handlers[entityType] = {};
    }
    this.handlers[entityType][action] = handler;
  }

  async queueCreate(entityType, data, { orgId } = {}) {
    const localRecord = await dataService.upsertLocal(entityType, { ...data, orgId }, {
      syncStatus: SYNC_STATUS.PENDING_CREATE
    });

    const recordData = localRecord.data || data;
    const entityId = this._extractId(entityType, recordData) || localRecord.id;
    await db.addToSyncQueue(SYNC_ACTIONS.CREATE, entityType, entityId, {
      data: recordData
    });

    return localRecord;
  }

  async queueUpdate(entityType, id, updates) {
    await dataService.upsertLocal(entityType, { ...updates, [this._idField(entityType)]: id }, {
      syncStatus: SYNC_STATUS.PENDING_UPDATE
    });

    await db.addToSyncQueue(SYNC_ACTIONS.UPDATE, entityType, id, {
      data: updates
    });
  }

  async queueDelete(entityType, id) {
    await dataService.setSyncStatus(entityType, id, SYNC_STATUS.PENDING_DELETE);
    await db.addToSyncQueue(SYNC_ACTIONS.DELETE, entityType, id, {});
  }

  async cancelQueuedCreate(entityType, id) {
    const items = await db.getSyncQueueItems();
    for (const item of items) {
      if (item.entity === entityType && item.action === SYNC_ACTIONS.CREATE && item.entityId === id) {
        await db.removeFromSyncQueue(item.queueId);
      }
    }
  }

  async processQueue() {
    if (this.isProcessing) return { skipped: true, reason: 'already-processing' };
    if (!navigator.onLine) return { skipped: true, reason: 'offline' };

    this.isProcessing = true;

    try {
      const items = await db.getSyncQueueItems();
      for (const item of items) {
        await this._processItem(item);
      }
      return { success: true };
    } finally {
      this.isProcessing = false;
    }
  }

  startBackgroundSync() {
    if (this.intervalId) return;
    this.intervalId = window.setInterval(() => {
      this.processQueue().catch((error) => {
        console.error('Background sync failed', error);
      });
    }, PROCESS_INTERVAL);
  }

  stopBackgroundSync() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  async _processItem(item) {
    const handler = this.handlers[item.entity]?.[item.action];
    if (!handler) {
      console.warn('No sync handler registered for', item.entity, item.action);
      await db.removeFromSyncQueue(item.queueId);
      return;
    }

    try {
      let handlerInput = item;
      if (item.action === SYNC_ACTIONS.CREATE) {
        const currentRecord = await dataService.getEntity(item.entity, item.entityId);
        if (currentRecord) {
          handlerInput = { ...item, payload: { data: currentRecord } };
        }
      }

      const result = await handler(handlerInput);
      if (item.action === SYNC_ACTIONS.DELETE) {
        await dataService.removeEntity(item.entity, item.entityId);
      } else if (result?.record) {
        const record = result.record;
        const serverId = this._extractId(item.entity, record);
        const localId = item.entityId;

        if (serverId) {
          await dataService.upsertFromServer(item.entity, [record]);
          if (localId) {
            if (serverId !== localId) {
              await dataService.removeEntity(item.entity, localId);
            } else {
              await dataService.markAsSynced(item.entity, localId, record);
            }
          }
        } else {
          await dataService.markAsSynced(item.entity, item.entityId, null);
        }
      } else {
        await dataService.markAsSynced(item.entity, item.entityId, null);
      }

      await db.removeFromSyncQueue(item.queueId);
    } catch (error) {
      console.error('Sync item failed', item, error);
      await this._handleSyncFailure(item, error);
    }
  }

  async _handleSyncFailure(item, error) {
    const retryCount = (item.retryCount || 0) + 1;
    await db.syncQueue.update(item.queueId, {
      retryCount,
      lastAttempt: Date.now()
    });

    if (retryCount >= MAX_RETRIES) {
      await dataService.setSyncStatus(item.entity, item.entityId, SYNC_STATUS.CONFLICT);
      await db.removeFromSyncQueue(item.queueId);
      console.error('Sync item marked as conflict after max retries', item.queueId);
    }
  }

  _idField(entityType) {
    switch (entityType) {
      case ENTITY_TYPES.CUSTOMER:
        return 'customerId';
      case ENTITY_TYPES.PRODUCT:
        return 'productId';
      case ENTITY_TYPES.ORDER:
        return 'orderId';
      case ENTITY_TYPES.EXPENSE:
        return 'expenseId';
      default:
        return 'id';
    }
  }

  _extractId(entityType, record) {
    if (!record) return null;
    const field = this._idField(entityType);
    return record[field] || record.id || null;
  }
}

export const syncService = new SyncService();


